import { isDefined } from "@app/helpers/kindOf";
import { IBase, ICatalog, IShop, sortByPosition } from "@app/common";
import { ICatalogProductToProductVariation } from '@app/common/src/interfaces/catalog/catalog-product-to-product-variation.interface';
import { ICatalogToProduct } from '@app/common/src/interfaces/catalog/catalog-to-product.interface';
import { UseFormReturn } from "react-hook-form";
import { sansLesClesPriveesDeCopie } from "../Duplication/cles-privees";

export interface CatalogFormValues extends Partial<ICatalog> {
  image: any;
}

export const transformCatalogFormValuesToPayload = (values: Omit<Partial<ICatalog>, keyof IBase>): any => {
  // `copieDe` ne sert qu'au panneau de création : il ne part jamais au serveur.
  return {
    ...sansLesClesPriveesDeCopie(values),
    catalogToTags: (values.catalogToTags || []).map((catalogToTag) => ({
      ...catalogToTag,
      tag: { _id: catalogToTag.tag._id }
    })),
    catalogToExcludedSteps: (values.catalogToExcludedSteps || []).map((catalogToExcludedStep) => ({
      ...catalogToExcludedStep,
      step: { _id: catalogToExcludedStep.step._id }
    })),
    catalogToExcludedOptions: (values.catalogToExcludedOptions || []).map((catalogToExcludedOption) => ({
      ...catalogToExcludedOption,
      option: { _id: catalogToExcludedOption.option._id }
    })),
    catalogToShops: (values.catalogToShops as unknown as { _id: string, catalogShopId?: string }[]).map((item) => ({
      _id: item.catalogShopId,
      shopId: item._id
    })),
    catalogToPacks: (values.catalogToPacks || []).map((catalogToPack, index) => ({
      ...catalogToPack,
      position: index,
      pack: { _id: catalogToPack.pack._id }
    })),
    catalogToProducts: (values.catalogToProducts || []).map((catalogToProduct, index) => ({
      ...catalogToProduct,
      position: index,
      product: { _id: catalogToProduct.product._id },
      catalogProductToProductVariations: catalogToProduct.catalogProductToProductVariations.map(catalogProductToProductVariation => ({
        ...catalogProductToProductVariation,
        productVariation: { _id: catalogProductToProductVariation.productVariation._id }
      }))
    }))
  }
}

export const useCatalogFormSubmit = () => {
  return async (formMethods: UseFormReturn<any, any>, submitCb: (payload: FormData) => void) => {
    const values = formMethods.getValues();

    const payload = transformCatalogFormValuesToPayload(values);

    const formData = new FormData();

    for (const [key, value] of Object.entries(payload) as any) {
      if (key === 'ticketInfos') {
        for (let [nestedKey, nestedValue] of Object.entries(value) as any) {
          if (isDefined(nestedValue)) {
            formData.append(`${key}[${nestedKey}]`, nestedValue as string);
          }
        }
      } else {
        formData.append(key, Array.isArray(value) ? JSON.stringify(value) : value as string);
      }
    }

    const isValid = await formMethods.trigger();

    if (isValid) {
      submitCb(formData);
    }
  }
}

// Les catalogProductToProductVariations sont créer au moment ou l’on met le produit dans le catalog en se basant sur les productVariations.
// Si on ajoute une productVariation après cette étape, cette derniere ne sera pas créer.
export const transformToUpdatableFormCatalogToProduct = (catalogToProduct: ICatalogToProduct) => {
  const existingProductVariationsIds = catalogToProduct?.catalogProductToProductVariations
    .map(catalogProductToProductVariation => catalogProductToProductVariation.productVariation._id);

  const missingProductVariations = catalogToProduct.product.productVariations
    .filter(productVariation => !existingProductVariationsIds.includes(productVariation._id));

  if (!missingProductVariations.length) {
    return catalogToProduct;
  }

  const missingCatalogProductToProductVariations = missingProductVariations.map(productVariation => ({
    price: null,
    deliveryPrice: null,
    takeAwayPrice: null,
    onSpotPrice: null,
    disabled: false,
    productVariation
  })) as unknown as ICatalogProductToProductVariation[];

  const catalogProductToProductVariations = [
    ...catalogToProduct.catalogProductToProductVariations,
    ...missingCatalogProductToProductVariations
  ];

  return {
    ...catalogToProduct,
    catalogProductToProductVariations
  }
}

export const transformToUpdatableFormCatalog = (catalog: Partial<ICatalog> | undefined, shops: IShop[] = []): CatalogFormValues => {
  return {
    name: catalog?.name,
    image: null,
    ticketInfos: catalog?.ticketInfos,
    catalogToShops: catalog?.catalogToShops?.map(catalogToShop => {
      const shop = shops.find(shop => shop._id === catalogToShop.shopId);

      return {
        ...catalogToShop,
        catalogShopId: catalogToShop._id,
        _id: catalogToShop.shopId,
        publicName: shop?.publicName || ''
      }
    }),
    catalogToExcludedSteps: catalog?.catalogToExcludedSteps,
    catalogToExcludedOptions: catalog?.catalogToExcludedOptions,
    catalogToConnectors: catalog?.catalogToConnectors,
    catalogToPromotions: catalog?.catalogToPromotions,
    catalogToTags: catalog?.catalogToTags,
    catalogToPacks: [...(catalog?.catalogToPacks || [])]
      .sort((catalogToPack1, catalogToPack2) => {
        if (typeof catalogToPack1?.position === 'number' || typeof catalogToPack2?.position === 'number') {
          return sortByPosition(catalogToPack1, catalogToPack2);
        }

        return catalogToPack1.pack?.name.localeCompare(catalogToPack2.pack?.name);
      }),
    catalogToProducts: [...(catalog?.catalogToProducts || [])]
      .map(transformToUpdatableFormCatalogToProduct)
      .sort((catalogToProduct1, catalogToProduct2) => {
        if (typeof catalogToProduct1?.position === 'number' || typeof catalogToProduct2?.position === 'number') {
          return sortByPosition(catalogToProduct1, catalogToProduct2);
        }

        return catalogToProduct1.product?.name.localeCompare(catalogToProduct2.product?.name);
      })
  }
}
